import { plural } from '../speak';

const count = (n: number, one: string) => plural('en', n, { one: `# ${one}`, other: `# ${one}s` });
const at = (where: string) => (where ? ` ${where}` : '');
type Kind = 'page' | 'tab' | 'section';
type HeaderKind = 'button' | 'counter' | 'badge' | 'ribbon' | 'alert';
const LABELS: Record<string, string> = { above: 'above their boxes', beside: 'beside their boxes', hidden: 'inside their boxes' };

/**
 * What changed since the last version, line by line, as Publish lists it:
 * a part added, renamed, moved, a setting changed. Names are the page's
 * own, quoted; `where` says where a new part went ("beside “First name”").
 */
export const changes = {
  firstList: (columns: number) => `The first version: ${count(columns, 'column')}`,
  firstSurvey: (questions: number, pages: number) => `The first version: ${count(questions, 'question')} on ${count(pages, 'page')}`,
  firstScreen: (fields: number, sections: number) => `The first version: ${count(fields, 'field')} in ${count(sections, 'section')}`,
  title: (was: string, now: string) => `Title: “${was}” → “${now}”`,
  description: 'The description changed',
  addedPart: (kind: Kind, name: string, where: string) => `Added the ${kind} “${name}”${at(where)}`,
  removedPart: (kind: Kind, name: string) => `Removed the ${kind} “${name}”`,
  renamedPart: (kind: Kind, was: string, now: string) => `Renamed the ${kind} “${was}” to “${now}”`,
  showsForSome: (name: string, survey: boolean) => `“${name}” now shows only for some ${survey ? 'answers' : 'records'}`,
  alwaysShows: (name: string) => `“${name}” now always shows`,
  whenShowsChanged: (name: string) => `“${name}”: when it shows changed`,
  added: (name: string, where: string) => `Added “${name}”${at(where)}`,
  removed: (name: string) => `Removed “${name}”`,
  renamed: (was: string, now: string) => `Renamed “${was}” to “${now}”`,
  helpChanged: (name: string) => `“${name}”: its help changed`,
  nowRequired: (name: string) => `“${name}” is now required`,
  noLongerRequired: (name: string) => `“${name}” is no longer required`,
  nowKind: (name: string, kind: string) => `“${name}” is now ${kind}`,
  nowOwnWay: (name: string) => `“${name}” is now shown its own way`,
  addedOption: (name: string, option: string) => `“${name}”: added the option “${option}”`,
  removedOption: (name: string, option: string) => `“${name}”: removed the option “${option}”`,
  optionsChanged: (name: string) => `“${name}”: its options changed`,
  takesOther: (name: string) => `“${name}”: takes an answer of its own (“Other”)`,
  noOther: (name: string) => `“${name}”: no longer takes an answer of its own`,
  showsChanged: (name: string) => `“${name}”: how it shows changed`,
  moved: (name: string, to: string) => `Moved “${name}” to “${to}”`,
  reorderedQuestions: (page: string) => `Reordered the questions on “${page}”`,
  reorderedFields: (group: string) => `Reordered the fields in “${group}”`,
  other: 'Other changes to the page’s settings',
  // ---- files
  filesTaken: (name: string) => `“${name}”: the files it takes changed`,
  severalFiles: (name: string) => `“${name}” now takes several files`,
  oneFile: (name: string) => `“${name}” takes one file again`,
  filesFromTo: (name: string, least: number, most: number) => `“${name}”: from ${least} to ${most} files`,
  filesUpTo: (name: string, most: number) => `“${name}”: up to ${most} files`,
  filesAtLeast: (name: string, least: number) => `“${name}”: at least ${least} files`,
  filesAny: (name: string) => `“${name}”: any number of files`,
  // A photo set's counts, said as photos (the field's own noun).
  photosFromTo: (name: string, least: number, most: number) => `“${name}”: from ${least} to ${most} photos`,
  photosUpTo: (name: string, most: number) => `“${name}”: up to ${most} photos`,
  photosAtLeast: (name: string, least: number) => `“${name}”: at least ${least} photos`,
  photosAny: (name: string) => `“${name}”: any number of photos`,
  filesShownAs: (name: string, as: 'list' | 'thumbnails' | 'cards') => `“${name}”: chosen files shown as ${as === 'list' ? 'a list' : as}`,
  filesSwitch: (name: string, on: boolean) => `“${name}”: people can ${on ? '' : 'no longer '}switch how chosen files show`,
  camera: (name: string, front: boolean) => `“${name}”: phones offer the ${front ? 'front' : 'rear'} camera`,
  noCamera: (name: string) => `“${name}”: phones no longer offer the camera`,
  recorded: (name: string, what: string) => `“${name}”: ${what} recorded on the page`,
  notRecorded: (name: string) => `“${name}”: nothing recorded on the page any more`,
  recordingUpTo: (name: string, seconds: number | null) => (seconds ? `“${name}”: a recording up to ${seconds >= 60 && seconds % 60 === 0 ? `${seconds / 60} min` : `${seconds} seconds`}` : `“${name}”: a recording as long as usual`),
  photosSmaller: (name: string, side: number, percent: number) => `“${name}”: photos are made smaller, to ${side} px at ${percent}%`,
  photosAsTaken: (name: string) => `“${name}”: photos are kept as they were taken`,
  photosUsual: (name: string) => `“${name}”: photos are made smaller as usual`,
  /** A photo set's own: its cover, its captions, crop and turn, the original kept, the shape of a crop (null: any). */
  photoCover: (name: string, on: boolean) => `“${name}”: ${on ? 'the first photo is the cover' : 'no photo is marked the cover'}`,
  photoCaptions: (name: string, on: boolean) => `“${name}”: ${on ? 'a caption under each photo' : 'no caption under the photos'}`,
  photoEdit: (name: string, on: boolean) => `“${name}”: photos ${on ? 'can be' : 'can no longer be'} cropped and turned`,
  photoOriginal: (name: string, on: boolean) => `“${name}”: a photo cropped ${on ? 'keeps' : 'no longer keeps'} its original`,
  photoShape: (name: string, shape: string | null) => `“${name}”: photos are cropped to ${shape ?? 'any shape'}`,
  // ---- a sheet's header, a list
  addedHeader: (kind: HeaderKind, label: string) => `Added the ${kind} “${label}”`,
  removedHeader: (kind: HeaderKind, label: string) => `Removed the ${kind} “${label}”`,
  renamedHeader: (kind: HeaderKind, was: string, now: string) => `Renamed the ${kind} “${was}” to “${now}”`,
  statusFrom: (label: string) => `Status steps from “${label}”`,
  noStatus: 'Removed the status steps',
  addedColumn: (label: string) => `Added the column “${label}”`,
  removedColumn: (label: string) => `Removed the column “${label}”`,
  reorderedColumns: 'Reordered the columns',
  rowsOnPage: (was: number, now: number) => `Rows on a page: ${was} → ${now}`,
  listOrder: 'The order of the list changed',
  searchLooks: 'Where the search looks changed',
  addedFilter: (label: string) => `Added the filter “${label}”`,
  removedFilter: (label: string) => `Removed the filter “${label}”`,
  groupings: 'The groupings changed',
  addedButton: (label: string) => `Added the button “${label}”`,
  removedButton: (label: string) => `Removed the button “${label}”`,
  // ---- the layout
  beside: (name: string) => `beside ${name}`,
  under: (name: string) => `under ${name}`,
  above: (name: string) => `above ${name}`,
  grouped: (n: number, group: string) => `Grouped ${n} into “${group}”`,
  madeTabs: (n: number, tabs: string) => `Made ${n} tabs: ${tabs}`,
  ungrouped: (group: string) => `Ungrouped “${group}”`,
  ungroupedTabs: (tabs: string) => `Ungrouped the tabs ${tabs}`,
  ungroupedArrangement: (parts: string) => `Ungrouped ${parts}`,
  put: (parts: string, where: string) => `Put ${parts} ${where}`,
  putUnder: (parts: string) => `Put ${parts} one under the other`,
  putSide: (parts: string) => `Put ${parts} side by side`,
  movedTo: (parts: string, to: string) => `Moved ${parts} to “${to}”`,
  /** A block, as the lines name it: "the heading “Contact”". */
  block: {
    heading: (text: string) => `the heading “${text}”`,
    words: (text: string) => `the words “${text}”`,
    button: (label: string) => `the button “${label}”`,
    image: (alt: string) => `the image “${alt}”`,
    anImage: 'an image',
    map: (label: string) => `the map “${label}”`,
    related: (label: string) => `the related list “${label}”`,
    divider: 'a divider',
    spacer: 'a spacer',
    form: (name: string) => `the saved form “${name}”`,
  },
  addedBlock: (block: string, where: string) => `Added ${block}${at(where)}`,
  changedBlockTo: (block: string, text: string) => `Changed ${block} to “${text}”`,
  renamedButton: (was: string, now: string) => `Renamed the button “${was}” to “${now}”`,
  changedBlock: (block: string) => `Changed ${block}`,
  removedBlock: (block: string) => `Removed ${block}`,
  twelfths: (name: string) => `${name}: rows divided in twelfths`,
  /** A group's columns: on every screen, or on a desktop and then the smaller ones; 12 is twelfths. */
  columns: (name: string, wide: number, medium?: number, narrow?: number) => {
    const n = (c: number) => (c === 12 ? 'twelfths' : count(c, 'column'));
    const smaller = [medium ? `${medium === 12 ? 'twelfths' : medium} on a tablet` : '', narrow ? `${narrow === 12 ? 'twelfths' : narrow} on a phone` : ''].filter(Boolean);
    return `${name}: ${smaller.length ? `${n(wide)} on a desktop, ${smaller.join(', ')}` : n(wide)}`;
  },
  gaps: (name: string, gaps: boolean) => `${name}: ${gaps ? 'rows may leave gaps' : 'rows kept full'}`,
  drawn: (name: string, style: 'card' | 'plain' | 'line' | 'framed' | 'inline') =>
    `${name}: drawn ${{ card: 'as a card', plain: 'plain, with no box', line: 'with a line under its title', framed: 'in a frame, its title on it', inline: 'on one line, its title its label' }[style]}`,
  folds: (name: string, fold: 'no' | 'open' | 'folded') => `${name}: ${{ no: 'no longer folds', open: 'folds by its title, starting open', folded: 'folds by its title, starting folded' }[fold]}`,
  labels: (name: string, place: string) => `${name}: labels ${LABELS[place] ?? 'where the page puts them'}`,
  labelWidth: (name: string, px: number) => `${name}: labels ${px ? `${px} px wide` : 'as wide as the page has them'}`,
  share: (name: string, share: string) => `${name}: ${share}`,
  columnsWide: (name: string, n: number) => `${name}: ${count(n, 'column')} wide`,
  itsLabel: (name: string, place: string) => `${name}: its label ${({ above: 'above its box', beside: 'beside its box', hidden: 'inside its box' } as Record<string, string>)[place] ?? 'where its group puts it'}`,
  placeholder: (name: string) => `${name}: its placeholder changed`,
  filledIn: (name: string) => `${name}: what the browser fills it in with changed`,
  keyboard: (name: string) => `${name}: its keyboard on a phone changed`,
  botCheck: (now: 'on' | 'off' | 'changed') => ({ on: 'The bot check is on', off: 'The bot check is off', changed: 'The bot check changed' })[now],
  printout: 'The printout’s header, footer or logo changed',
  duplicates: (now: 'on' | 'off' | 'changed') => ({ on: 'Possible duplicates are found as records are typed', off: 'Possible duplicates are no longer found', changed: 'How possible duplicates are found changed' })[now],
  records: (other: string | null) => (other ? `Its records are called “${other}” in Fieldia’s sentences` : 'Its records are called records again'),
  smallLabels: (on: boolean): string => (on ? 'Field names are written small inside Fieldia’s sentences' : 'Field names are written as they are inside Fieldia’s sentences'),
  limits: (now: 'on' | 'off' | 'changed') => ({ on: 'It takes answers only when and as many as it says', off: 'It takes answers at any time, as many as come', changed: 'When it takes answers, and how many, changed' })[now],
  continueLater: (on: boolean): string => (on ? 'It offers Continue later' : 'It no longer offers Continue later'),
  approval: (now: 'on' | 'off' | 'changed') => ({ on: 'Its records wait for approval', off: 'Its records no longer wait for approval', changed: 'What stays open while a record waits for approval changed' })[now],
  suggest: (now: 'on' | 'off' | 'changed') => ({ on: 'It offers Fill from text, by your app’s AI', off: 'It no longer offers Fill from text', changed: 'How Fill from text fills the form changed' })[now],
  quiz: (now: 'on' | 'off' | 'changed') => ({ on: 'It shows a quiz’s results', off: 'It no longer shows a quiz’s results', changed: 'How it shows the quiz’s results changed' })[now],
  review: (now: 'on' | 'off' | 'changed') => ({ on: 'It shows every answer before it is sent', off: 'It no longer shows the answers before they are sent', changed: 'Its review before sending changed' })[now],
  flowOne: (on: boolean): string => (on ? 'It asks one question at a time' : 'It asks its questions together again'),
  questionsDrawn: 'Which questions each person is asked, or their order, changed',
  timeLimit: 'Its time limits changed',
  outbox: (on: boolean): string => (on ? 'It keeps sends made without signal on the device' : 'It no longer keeps sends made without signal'),
  payment: (now: 'on' | 'off' | 'changed'): string => ({ on: 'It takes a payment before the answers go', off: 'It no longer takes a payment', changed: 'Its payment changed' })[now],
  prefill: (count: number): string => (count ? `The link it is opened with fills ${count === 1 ? 'a field' : `${count} fields`}` : 'The link it is opened with fills no field'),
  thePage: 'the page',
  // ---- the look
  look: {
    accent: 'The accent colour',
    font: 'The font',
    density: 'The spacing',
    corners: 'The corners',
    labels: 'Where labels sit',
    labelWidth: 'Labels set beside',
    scheme: 'The colours',
  },
  /** A setting of the look as it was or is now; null for none, as the skin has it. */
  lookValue: (key: string, value: string | number | null) => {
    if (value === null) return key === 'labelWidth' ? 'as wide as the skin has them' : ['corners', 'labels', 'scheme'].includes(key) ? 'as the skin has them' : 'as the skin has it';
    if (key === 'labelWidth') return `${value} px wide`;
    const words: Record<string, Record<string, string>> = { font: { system: 'the system’s' }, labels: LABELS, scheme: { auto: 'as the reader’s system has them' } };
    return words[key]?.[String(value)] ?? String(value);
  },
  lookLine: (setting: string, was: string, now: string) => `${setting}: ${was} → ${now}`,
  // ---- rules
  ruleChanged: (name: string, must: string) => `“${name}”: a rule changed — ${must}`,
  ruleRemoved: (name: string, must: string) => `“${name}”: removed the rule — ${must}`,
  ruleAdded: (name: string, must: string) => `“${name}”: a rule — ${must}`,
  workedOut: (name: string, formula: string) => `“${name}” is worked out from ${formula}`,
  noLongerWorkedOut: (name: string) => `“${name}” is no longer worked out`,
  setWhen: (name: string, value: string, when: string) => `“${name}” is set to ${value} when ${when}`,
  setWhenJoin: (lines: readonly string[]) => lines.join('; '),
  noLongerSet: (name: string) => `“${name}” is no longer set by a rule`,
  // ---- translations
  writtenIn: (language: string) => `The page is now written in ${language}`,
  addedLanguage: (language: string) => `Added ${language}`,
  removedLanguage: (language: string) => `Removed ${language}`,
  translated: (language: string, n: number) => `${language}: ${count(n, 'word')} translated`,
  translationsChanged: (language: string, n: number) => `${language}: ${count(n, 'translation')} changed`,
  translationsTakenOut: (language: string, n: number) => `${language}: ${count(n, 'translation')} taken out`,
  // ---- a structure's own
  offersEvery: (name: string) => `“${name}”: offers every record`,
  offersWhere: (name: string, field: string, value: string) => `“${name}”: offers only records where ${field} is ${value}`,
  offersChanged: (name: string) => `“${name}”: the records it offers changed`,
  addsUp: (name: string, columns: string) => `“${name}”: adds up ${columns}`,
  addsUpNothing: (name: string) => `“${name}”: adds up nothing`,
  optionalColumns: (name: string) => `“${name}”: the columns people may hide changed`,
};
